import { useQuery, useReactiveVar } from '@apollo/client'
import React, { useRef, useState } from 'react'

import { LoadingSpinner } from '@src/components/Loaders/LoadingSpinner'
import { breakpoints } from '@src/constants/breakpoints'
import { screenResolutionVar } from '@src/models/screenResolution'
import { imageJitURL } from '@src/utils/imageJitURL'
import { useIOSKeyboardFix } from '@src/hooks/useIOSKeyboardFix'

import { AddMenuItemForm } from './AddMenuItemForm'
import { SingleMenuItemModalContainer } from './MenuItem.modal.styles'
import { outletMenuItemOptionsDocument } from './queries/__generated__/outletMenuItemOptions.graphql-interface'
import { GetOutletMenuItemQuery } from '../MenuItem/queries/__generated__/GetOutletMenuItem.graphql-interface'

export const MenuItemModal: React.FC<{
  outletMenuItem: GetOutletMenuItemQuery['outletMenuItem']
  closeModal: () => void
}> = ({ outletMenuItem, closeModal }) => {
  const [showModalFooter, setShowModalFooter] = useState(false)
  const screenResolution = useReactiveVar(screenResolutionVar)

  const { isKeyboardOpen } = useIOSKeyboardFix()

  const { data, loading, error } = useQuery(outletMenuItemOptionsDocument, {
    variables: {
      outletMenuItemId: outletMenuItem.id,
    },
  })

  const contentRef = useRef<HTMLDivElement>(null)

  const scrollToFooter = (): void => {
    contentRef?.current?.scrollTo({
      top: contentRef.current.offsetTop + 144,
      left: 0,
      behavior: 'smooth',
    })
  }

  if (loading) return <LoadingSpinner />
  if (error || !data) {
    return null
  }

  const image = outletMenuItem.image
  const featuredImages = outletMenuItem.featuredImages

  const getImageWidth = () => {
    if (screenResolution.width < breakpoints.tablet) {
      return 300 * 2
    } else {
      return 600 * 2
    }
  }

  const getImageHeight = () => {
    if (screenResolution.width < breakpoints.tablet) {
      return 169 * 2
    } else {
      return 338 * 2
    }
  }

  const imageURL = image
    ? imageJitURL(image, {
        resize: {
          width: getImageWidth(),
          height: getImageHeight(),
        },
        toFormat: 'jpeg',
      })
    : null

  const featuredImageURLs = featuredImages.map(image =>
    image.src
      ? {
          image: imageJitURL(image.src, {
            resize: {
              width: getImageWidth(),
              height: getImageHeight(),
            },
            toFormat: 'jpeg',
          }),
          caption: image.caption ?? '',
        }
      : null
  )

  if (imageURL && featuredImageURLs.length) {
    featuredImageURLs.unshift({ image: imageURL, caption: '' })
  }

  return (
    <SingleMenuItemModalContainer
      ref={contentRef}
      showModalFooter={showModalFooter}
      isIOSKeyboardOpen={isKeyboardOpen}
    >
      <AddMenuItemForm
        outletMenuItem={outletMenuItem}
        options={data.optionsByOutletMenuItemId}
        imageURL={imageURL}
        showModalFooter={showModalFooter}
        setShowModalFooter={setShowModalFooter}
        featuredImageURLs={featuredImageURLs}
        close={closeModal}
        scrollToFooter={scrollToFooter}
        narrowFulfilmentMethods={
          outletMenuItem.parentMenu.narrowFulfilmentMethods
        }
      />
    </SingleMenuItemModalContainer>
  )
}
